import { ThresholdStatus } from '../features/status';
import { getLineStyleForDefaultThresholdLine } from '../features/threshold/getters';
import { DeltaLabelStyle } from '../features/threshold/models';
import { Messages } from '../localization';
import { ComparisonDelta, EffectiveDefinition } from '../models';
import { formatAbsoluteValue, formatValueAsUnsignedPercentage } from './numberFormatter';
import { symbolForDelta } from './symbolForDelta';

interface ComparisonText {
  /**
   * `symbol` and `text` are left behind to be used solely by metrics in Phoenix. The separaration
   * is necessary there for styling purposes. Hopefully, the arrow pointing up/down unicode symbols
   * will eventually become icons, and at that time, Phoenix should refactor as well. *Then*
   * `symbol` and `text` should no longer be referenced anywhere.
   */
  symbol?: string;
  text: string;
  comparisonText: string;
  accessibilityText: string;
}

export const formatComparisonTextForCard = (
  comparison: ComparisonDelta,
  definition: EffectiveDefinition,
  formatString?: string,
  status?: ThresholdStatus
): ComparisonText => {
  const style = getLineStyleForDefaultThresholdLine(definition);
  switch (style?.deltaLabel) {
    case DeltaLabelStyle.TARGET_VALUE:
      return formatTargetDeltaTextForCard(comparison, formatString, status);
    case DeltaLabelStyle.HISTORICAL_DELTA:
    case undefined:
    default:
      return formatDeltaTextForCard(comparison);
  }
};

const formatTargetDeltaTextForCard = (
  comparison: ComparisonDelta,
  formatString?: string,
  status?: ThresholdStatus
): ComparisonText => {
  const iconAccessibilityText = getAccessibilityTextForStatusIcon(status ?? ThresholdStatus.UNKNOWN);
  const accessibilityTextPrefix = iconAccessibilityText + (iconAccessibilityText !== '' ? ' ' : '');

  if (comparison.absolute === 0) {
    return {
      text: Messages.metricsComparisonAtTarget(),
      comparisonText: Messages.metricsComparisonAtTarget(),
      accessibilityText: accessibilityTextPrefix + Messages.metricsComparisonAtTarget(),
    };
  }

  const formattedValue = formatAbsoluteValue(comparison.absolute, formatString);

  const targetText =
    comparison.absolute < 0
      ? Messages.metricsComparisonBelowTarget({ value: formattedValue })
      : Messages.metricsComparisonAboveTarget({ value: formattedValue });

  return {
    text: targetText,
    comparisonText: targetText,
    accessibilityText: accessibilityTextPrefix + targetText,
  };
};

const formatDeltaTextForCard = (comparison: ComparisonDelta): ComparisonText => {
  if (comparison.absolute === 0) {
    return {
      text: Messages.metricsComparisonNoChange(),
      comparisonText: Messages.metricsComparisonNoChange(),
      accessibilityText: Messages.metricsComparisonNoChange(),
    };
  }

  const symbol = symbolForDelta(comparison);
  const text = formatValueAsUnsignedPercentage(comparison.percentage);
  const direction = symbol !== '' ? (comparison.absolute >= 0 ? '+' : '-') : '';

  return {
    symbol: symbol,
    text: text,
    comparisonText: symbol + text,
    accessibilityText: direction + text,
  };
};

export const formatComparisonTextForDetail = (
  comparison: ComparisonDelta,
  definition: EffectiveDefinition,
  formatString?: string,
  status?: ThresholdStatus
): ComparisonText => {
  const style = getLineStyleForDefaultThresholdLine(definition);
  switch (style?.deltaLabel) {
    case DeltaLabelStyle.TARGET_VALUE:
      return formatTargetDeltaText(comparison, formatString, status);
    case DeltaLabelStyle.HISTORICAL_DELTA:
    case undefined:
    default:
      return formatDeltaText(comparison, formatString);
  }
};

const formatTargetDeltaText = (
  comparison: ComparisonDelta,
  formatString?: string,
  status?: ThresholdStatus
): ComparisonText => {
  const iconAccessibilityText = getAccessibilityTextForStatusIcon(status ?? ThresholdStatus.UNKNOWN);
  const accessibilityTextPrefix = iconAccessibilityText + (iconAccessibilityText !== '' ? ' ' : '');

  if (comparison.absolute === 0) {
    return {
      text: Messages.metricsComparisonAtTarget(),
      comparisonText: Messages.metricsComparisonAtTarget(),
      accessibilityText: accessibilityTextPrefix + Messages.metricsComparisonAtTarget(),
    };
  }

  const formattedValue = formatAbsoluteValue(comparison.absolute, formatString);

  const targetText =
    comparison.absolute < 0
      ? Messages.metricsComparisonBelowTargetDetail({ value: formattedValue })
      : Messages.metricsComparisonAboveTargetDetail({ value: formattedValue });

  return {
    text: targetText,
    comparisonText: targetText,
    accessibilityText: accessibilityTextPrefix + targetText,
  };
};

const formatDeltaText = (comparison: ComparisonDelta, formatString?: string): ComparisonText => {
  if (comparison.absolute === 0) {
    return {
      text: Messages.metricsComparisonNoChange(),
      comparisonText: Messages.metricsComparisonNoChange(),
      accessibilityText: Messages.metricsComparisonNoChange(),
    };
  }

  const symbol = symbolForDelta(comparison);
  const text = formatDeltaValues(comparison, formatString);
  const direction = symbol !== '' ? (comparison.absolute >= 0 ? '+' : '-') : '';

  return {
    symbol: symbol,
    text: text,
    comparisonText: symbol + text,
    accessibilityText: direction + text,
  };
};

const formatDeltaValues = (comparison: ComparisonDelta, formatString?: string): string => {
  // Since the comparison widget shows up/down arrows, we format them without a sign.
  const percentage = formatValueAsUnsignedPercentage(comparison.percentage);
  const absolute = formatAbsoluteValue(comparison.absolute, formatString || '');

  return `${percentage} (${absolute})`;
};

const getAccessibilityTextForStatusIcon = (status: ThresholdStatus): string => {
  switch (status) {
    case ThresholdStatus.GOOD:
      return Messages.metricsGoodStatusDescription();
    case ThresholdStatus.BAD:
      return Messages.metricsBadStatusDescription();
    case ThresholdStatus.NEUTRAL:
      return Messages.metricsNeutralStatusDescription();
    case ThresholdStatus.UNKNOWN:
    default:
      return '';
  }
};
